/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import VERTC, {
  MirrorType,
  StreamIndex,
  IRTCEngine,
  RoomProfileType,
  onUserJoinedEvent,
  onUserLeaveEvent,
  MediaType,
  LocalStreamStats,
  RemoteStreamStats,
  StreamRemoveReason,
  LocalAudioPropertiesInfo,
  RemoteAudioPropertiesInfo,
  AudioProfileType,
  DeviceInfo,
  AutoPlayFailedEvent,
  PlayerEvent,
  NetworkQuality,
  VideoRenderMode,
  ScreenEncoderConfig,
} from '@volcengine/rtc';
import RTCAIAnsExtension from '@volcengine/rtc/extension-ainr';
import { Message } from '@arco-design/web-react';
import Apis from '@/app/index';
import { string2tlv } from '@/utils/utils';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import store from '@/store'; // 导入 store
import { fetchAndInitHistoryMsg } from '@/store/slices/room'; // 导入 action

// 定义历史对话数据结构
export interface HistoricalMessage {
  id: string;
  userId: string;
  content: string;
  timestamp: number;
  role: string;
  // 可以根据实际接口返回添加更多字段

}

export interface IEventListener {
  handleError: (e: { errorCode: any }) => void;
  handleUserJoin: (e: onUserJoinedEvent) => void;
  handleUserLeave: (e: onUserLeaveEvent) => void;
  handleTrackEnded: (e: { kind: string; isScreen: boolean }) => void;
  handleUserPublishStream: (e: { userId: string; mediaType: MediaType }) => void;
  handleUserUnpublishStream: (e: {
    userId: string;
    mediaType: MediaType;
    reason: StreamRemoveReason;
  }) => void;
  handleRemoteStreamStats: (e: RemoteStreamStats) => void;
  handleLocalStreamStats: (e: LocalStreamStats) => void;
  handleLocalAudioPropertiesReport: (e: LocalAudioPropertiesInfo[]) => void;
  handleRemoteAudioPropertiesReport: (e: RemoteAudioPropertiesInfo[]) => void;
  handleAudioDeviceStateChanged: (e: DeviceInfo) => void;
  handleAutoPlayFail: (e: AutoPlayFailedEvent) => void;
  handlePlayerEvent: (e: PlayerEvent) => void;
  handleRoomBinaryMessageReceived: (e: { userId: string; message: ArrayBuffer }) => void;
  handleNetworkQuality: (
      uplinkNetworkQuality: NetworkQuality,
      downlinkNetworkQuality: NetworkQuality
  ) => void;
  // 新增：处理历史对话的回调
  handleHistoricalMessages?: (messages: HistoricalMessage[]) => void;
}

export interface BasicBody {
  app_id: string;
  room_id: string;
  user_id: string;
  token?: string;
}

interface UrlParams {
  [key: string]: string;
}

/**
 * @brief RTC Core Client
 * @notes Refer to official website documentation to get more information about the API.
 */
export class RTCClient {
  engine!: IRTCEngine;

  basicInfo!: BasicBody;

  private sessionId?: string;

  private _audioCaptureDevice?: string;

  private _videoCaptureDevice?: string;

  audioBotEnabled = false;

  audioBotStartTime = 0;

  // 存储历史对话
  private historicalMessages: HistoricalMessage[] = [];

  createEngine = async () => {
    this.engine = VERTC.createEngine(this.basicInfo.app_id);
    try {
      const AIAnsExtension = new RTCAIAnsExtension();
      await this.engine.registerExtension(AIAnsExtension);
      AIAnsExtension.enable();
    } catch (error) {
      console.warn(
          `当前环境不支持 AI 降噪, 此错误可忽略, 不影响实际使用, e: ${(error as any).message}`
      );
    }
  };

  addEventListeners = ({
                         handleError,
                         handleUserJoin,
                         handleUserLeave,
                         handleTrackEnded,
                         handleUserPublishStream,
                         handleUserUnpublishStream,
                         handleRemoteStreamStats,
                         handleLocalStreamStats,
                         handleLocalAudioPropertiesReport,
                         handleRemoteAudioPropertiesReport,
                         handleAudioDeviceStateChanged,
                         handleAutoPlayFail,
                         handlePlayerEvent,
                         handleRoomBinaryMessageReceived,
                         handleNetworkQuality,
                         handleHistoricalMessages
                       }: IEventListener) => {
    this.engine.on(VERTC.events.onError, handleError);
    this.engine.on(VERTC.events.onUserJoined, handleUserJoin);
    this.engine.on(VERTC.events.onUserLeave, handleUserLeave);
    this.engine.on(VERTC.events.onTrackEnded, handleTrackEnded);
    this.engine.on(VERTC.events.onUserPublishStream, handleUserPublishStream);
    this.engine.on(VERTC.events.onUserUnpublishStream, handleUserUnpublishStream);
    this.engine.on(VERTC.events.onRemoteStreamStats, handleRemoteStreamStats);
    this.engine.on(VERTC.events.onLocalStreamStats, handleLocalStreamStats);
    this.engine.on(VERTC.events.onAudioDeviceStateChanged, handleAudioDeviceStateChanged);
    this.engine.on(VERTC.events.onLocalAudioPropertiesReport, handleLocalAudioPropertiesReport);
    this.engine.on(VERTC.events.onRemoteAudioPropertiesReport, handleRemoteAudioPropertiesReport);
    this.engine.on(VERTC.events.onAutoplayFailed, handleAutoPlayFail);
    this.engine.on(VERTC.events.onPlayerEvent, handlePlayerEvent);
    this.engine.on(VERTC.events.onRoomBinaryMessageReceived, handleRoomBinaryMessageReceived);
    this.engine.on(VERTC.events.onNetworkQuality, handleNetworkQuality);
  };



  /**
   * 获取历史对话
   */
  private fetchHistoricalConversations = async (): Promise<HistoricalMessage[]> => {
    try {
      const urlParams = this.getUrlParams();
      // 构造请求参数，根据实际需要调整
      const params = new URLSearchParams({
        _c: 'englishnews',
        _a: 'subtitleList',
        room_id: this.basicInfo.room_id,
        user_id: this.basicInfo.user_id,
        // 可以添加其他必要参数，如lesson_id等
        lesson_id: urlParams.lesson_id || '',
      });

      const response = await fetch(`https://m.boyakids.com/?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });


      const data = await response.json();
      // 假设接口返回格式为 { code: 0, data: { messages: [...] } }
      if (data.msg) {
        this.historicalMessages = data.msg;
        return data.msg;
      }
      // 移除多余的else块，直接在if判断不成立时执行以下逻辑
      throw new Error(`获取历史对话失败: ${data.msg || '未知错误'}`);
      // 移除不可达的return []（throw之后不会执行后续代码）
    } catch (error) {
      console.error('获取历史对话出错:', error);
      Message.error(`加载历史对话失败: ${(error as Error).message}`);
      return [];
    }
  };

  joinRoom = async () => { // 改为 async 函数，确保能等待结果
    console.log(
        ' ------ userJoinRoom\n',
        `roomId: ${this.basicInfo.room_id}\n`,
        `uid: ${this.basicInfo.user_id}`
    );

    try {
      // 等待房间加入成功
      const joinResult = await this.engine.joinRoom(
          this.basicInfo.token!,
          `${this.basicInfo.room_id!}`,
          {
            userId: this.basicInfo.user_id!,
            extraInfo: JSON.stringify({
              call_scene: 'RTC-AIGC',
              user_name: this.basicInfo.user_id,
              user_id: this.basicInfo.user_id,
            }),
          },
          {
            isAutoPublish: true,
            isAutoSubscribeAudio: true,
            roomProfileType: RoomProfileType.chat,
          }
      );

      // 房间加入成功后，触发历史消息加载
      console.log('房间加入成功，开始加载历史消息');
      store.dispatch(fetchAndInitHistoryMsg())
          .then((formattedMessages) => {
            // console.log('历史消息加载成功:', formattedMessages);
          })
          .catch((error) => {
            console.error('历史消息加载失败:', error);
          });

      return joinResult;
    } catch (error) {
      console.error('房间加入失败:', error);
      throw error;
    }
  };

  leaveRoom = () => {
    if (!this.engine) {
      console.warn("RTC engine is not initialized, cannot leave room");
      return;
    }

    this.audioBotEnabled = false;
    this.engine.leaveRoom().catch(err => {
      console.error("Failed to leave room:", err);
    });
    VERTC.destroyEngine(this.engine);
    this._audioCaptureDevice = undefined;
    // 清空历史对话
    this.historicalMessages = [];
  };

  checkPermission(): Promise<{
    video: boolean;
    audio: boolean;
  }> {
    return VERTC.enableDevices({
      video: false,
      audio: true,
    });
  }

  /**
   * @brief get the devices
   * @returns
   */
  async getDevices(props?: { video?: boolean; audio?: boolean }): Promise<{
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
    videoInputs: MediaDeviceInfo[];
  }> {
    const { video = false, audio = true } = props || {};
    let audioInputs: MediaDeviceInfo[] = [];
    let audioOutputs: MediaDeviceInfo[] = [];
    let videoInputs: MediaDeviceInfo[] = [];
    const { video: hasVideoPermission, audio: hasAudioPermission } = await VERTC.enableDevices({
      video,
      audio,
    });
    if (audio) {
      const inputs = await VERTC.enumerateAudioCaptureDevices();
      const outputs = await VERTC.enumerateAudioPlaybackDevices();
      audioInputs = inputs.filter((i) => i.deviceId && i.kind === 'audioinput');
      audioOutputs = outputs.filter((i) => i.deviceId && i.kind === 'audiooutput');
      this._audioCaptureDevice = audioInputs.filter((i) => i.deviceId)?.[0]?.deviceId;
      if (hasAudioPermission) {
        // 保持原有逻辑
      } else {
        Message.error('暂无麦克风设备权限, 请先确认设备权限授予情况。');
      }
    }
    if (video) {
      videoInputs = await VERTC.enumerateVideoCaptureDevices();
      videoInputs = videoInputs.filter((i) => i.deviceId && i.kind === 'videoinput');
      this._videoCaptureDevice = videoInputs?.[0]?.deviceId;
      if (hasVideoPermission) {
        if (!videoInputs?.length) {
          Message.error('无摄像头设备, 请先确认设备情况。');
        }
      } else {
        Message.error('暂无摄像头设备权限, 请先确认设备权限授予情况。');
      }
    }

    return {
      audioInputs,
      audioOutputs,
      videoInputs,
    };
  }

  startVideoCapture = async (camera?: string) => {
    await this.engine.startVideoCapture(camera || this._videoCaptureDevice);
  };

  stopVideoCapture = async () => {
    this.engine.setLocalVideoMirrorType(MirrorType.MIRROR_TYPE_RENDER);
    await this.engine.stopVideoCapture();
  };

  startScreenCapture = async (enableAudio = false) => {
    await this.engine.startScreenCapture({
      enableAudio,
    });
  };

  stopScreenCapture = async () => {
    await this.engine.stopScreenCapture();
  };

  startAudioCapture = async (mic?: string) => {
    await this.engine.startAudioCapture(mic || this._audioCaptureDevice);
  };

  stopAudioCapture = async () => {
    await this.engine.stopAudioCapture();
  };

  publishStream = (mediaType: MediaType) => {
    this.engine.publishStream(mediaType);
  };

  unpublishStream = (mediaType: MediaType) => {
    this.engine.unpublishStream(mediaType);
  };

  publishScreenStream = async (mediaType: MediaType) => {
    await this.engine.publishScreen(mediaType);
  };

  unpublishScreenStream = async (mediaType: MediaType) => {
    await this.engine.unpublishScreen(mediaType);
  };

  setScreenEncoderConfig = async (description: ScreenEncoderConfig) => {
    await this.engine.setScreenEncoderConfig(description);
  };

  /**
   * @brief 设置业务标识参数
   * @param businessId
   */
  setBusinessId = (businessId: string) => {
    this.engine.setBusinessId(businessId);
  };

  setAudioVolume = (volume: number) => {
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_MAIN, volume);
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_SCREEN, volume);
  };

  /**
   * @brief 设置音质档位
   */
  setAudioProfile = (profile: AudioProfileType) => {
    this.engine.setAudioProfile(profile);
  };

  /**
   * @brief 切换设备
   */
  switchDevice = (deviceType: MediaType, deviceId: string) => {
    if (deviceType === MediaType.AUDIO) {
      this._audioCaptureDevice = deviceId;
      this.engine.setAudioCaptureDevice(deviceId);
    }
    if (deviceType === MediaType.VIDEO) {
      this._videoCaptureDevice = deviceId;
      this.engine.setVideoCaptureDevice(deviceId);
    }
    if (deviceType === MediaType.AUDIO_AND_VIDEO) {
      this._audioCaptureDevice = deviceId;
      this._videoCaptureDevice = deviceId;
      this.engine.setVideoCaptureDevice(deviceId);
      this.engine.setAudioCaptureDevice(deviceId);
    }
  };

  setLocalVideoMirrorType = (type: MirrorType) => {
    return this.engine.setLocalVideoMirrorType(type);
  };

  setLocalVideoPlayer = (
      userId: string,
      renderDom?: string | HTMLElement,
      isScreenShare = false,
      renderMode = VideoRenderMode.RENDER_MODE_FILL
  ) => {
    return this.engine.setLocalVideoPlayer(
        isScreenShare ? StreamIndex.STREAM_INDEX_SCREEN : StreamIndex.STREAM_INDEX_MAIN,
        {
          renderDom,
          userId,
          renderMode,
        }
    );
  };

  /**
   * @brief 移除播放器
   */
  removeVideoPlayer = (userId: string, scope: StreamIndex | 'Both' = 'Both') => {
    let removeScreen = scope === StreamIndex.STREAM_INDEX_SCREEN;
    let removeCamera = scope === StreamIndex.STREAM_INDEX_MAIN;
    if (scope === 'Both') {
      removeCamera = true;
      removeScreen = true;
    }
    if (removeScreen) {
      this.engine.setLocalVideoPlayer(StreamIndex.STREAM_INDEX_SCREEN, { userId });
    }
    if (removeCamera) {
      this.engine.setLocalVideoPlayer(StreamIndex.STREAM_INDEX_MAIN, { userId });
    }
  };

  getUrlParams(): UrlParams {
    const params: UrlParams = {};
    const url = window.location.href;
    const queryString = url.split('?')[1];

    if (queryString) {
      const paramPairs = queryString.split('&');
      paramPairs.forEach((pair) => {
        const [key, value] = pair.split('=');
        if (key) {
          params[key] = decodeURIComponent(value || '');
        }
      });
    }

    return params;
  }

  /**
   * @brief 启用 AIGC
   */
  startAgent = async (scene: string) => {
    const urlParams = this.getUrlParams();
    const LessonId = urlParams.lesson_id;
    const EnglishToken = urlParams.english_token;
    const SessionId = sessionStorage.getItem('sessionId');
    const SubtitleList = sessionStorage.getItem('subtitleList');


    if (this.audioBotEnabled) {
      await this.stopAgent(scene);
    }
    console.log('startAgent成功，获取到sessionId:', SessionId);
    if(SubtitleList){
      const historicalMessages = JSON.parse(SubtitleList) as HistoricalMessage[];
      console.log('初始化历史对话:', historicalMessages);
      // 同步到实例的历史对话数组中
      this.historicalMessages = historicalMessages;
    }



    await Apis.VoiceChat.StartVoiceChat({
      SceneID: scene,
      lessonId: LessonId,
      englishToken: EnglishToken,
      sessionId: SessionId
    });
    this.audioBotEnabled = true;
    this.audioBotStartTime = Date.now();
  };

  /**
   * @brief 关闭 AIGC
   */
  stopAgent = async (scene: string) => {
    const urlParams = this.getUrlParams();
    const LessonId = urlParams.lesson_id;
    const EnglishToken = urlParams.english_token;
    const SessionId = sessionStorage.getItem('sessionId');

    if (this.audioBotEnabled || sessionStorage.getItem('audioBotEnabled')) {
      await Apis.VoiceChat.StopVoiceChat({
        SceneID: scene,
        lessonId: LessonId,
        englishToken: EnglishToken,
        sessionId: SessionId
      });
      this.audioBotStartTime = 0;
      sessionStorage.removeItem('audioBotEnabled');
    }
    this.audioBotEnabled = false;
  };

  /**
   * @brief 命令 AIGC
   */
  commandAgent = ({
                    command,
                    agentName,
                    interruptMode = INTERRUPT_PRIORITY.NONE,
                    message = '',
                  }: {
    command: COMMAND;
    agentName: string;
    interruptMode?: INTERRUPT_PRIORITY;
    message?: string;
  }) => {
    if (this.audioBotEnabled) {
      this.engine.sendUserBinaryMessage(
          agentName,
          string2tlv(
              JSON.stringify({
                Command: command,
                InterruptMode: interruptMode,
                Message: message,
              }),
              'ctrl'
          )
      );
      return;
    }
    console.warn('Interrupt failed, bot not enabled.');
  };

  /**
   * @brief 更新 AIGC 配置
   */
  updateAgent = async (scene: string) => {
    if (this.audioBotEnabled) {
      await this.stopAgent(scene);
      await this.startAgent(scene);
    } else {
      await this.startAgent(scene);
    }
  };

  /**
   * @brief 获取当前 AI 是否启用
   */
  getAgentEnabled = () => {
    return this.audioBotEnabled;
  };

  /**
   * @brief 获取历史对话
   */
  getHistoricalMessages = (): HistoricalMessage[] => {
    return [...this.historicalMessages];
  };
}

export default new RTCClient();
